import { useCallback, useEffect, useRef, useState } from 'react';

import { Download, Printer } from 'lucide-react';

import { Button } from '@/_components';
import type { SvgIconProps } from '@/types';

import { ActionButton } from '../actions';

type DownloadPDFProps = {
  actionButtonTitle?: string;
  actionButton?: boolean;
  icon?: SvgIconProps;
  printMode?: boolean;
  mutateFunction: () => Promise<{ data: { name: string; pdf_url: string } }>;
};

export function DownloadPDF({
  icon,
  actionButtonTitle,
  actionButton = false,
  printMode = true,
  mutateFunction,
}: DownloadPDFProps) {
  const [isLoading, setIsPrinting] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    if (printMode && !iframeRef.current) {
      const iframe = document.createElement('iframe');
      iframe.id = 'pdfFrame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      iframeRef.current = iframe;
    }

    return () => {
      if (iframeRef.current) {
        iframeRef.current.remove();
        iframeRef.current = null;
      }
    };
  }, [printMode]);

  const openPdfInNewWindow = useCallback((pdfUrl: string) => {
    window.open(pdfUrl, '_blank');
  }, []);

  const setupPrintHandlers = useCallback(
    (pdfUrl: string) => {
      if (!iframeRef.current) return;

      iframeRef.current.onload = function () {
        try {
          if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.print();
          } else {
            throw new Error('Content window not available');
          }
        } catch (printError) {
          console.error('Error triggering print or unloading iframe:', printError);
        } finally {
          setIsPrinting(false);
        }
      };

      iframeRef.current.onerror = function () {
        console.error('Error loading PDF in iframe');
        openPdfInNewWindow(pdfUrl);
        setIsPrinting(false);
      };
    },
    [openPdfInNewWindow]
  );

  const printPdf = useCallback(
    async (pdfUrl: string) => {
      try {
        const response = await fetch(pdfUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();

        const blobUrl = URL.createObjectURL(blob);

        if (iframeRef.current) {
          iframeRef.current.src = blobUrl;
          setupPrintHandlers(pdfUrl);
        }
      } catch (fetchError) {
        console.error('Error fetching PDF:', fetchError);
        openPdfInNewWindow(pdfUrl);
      }
    },
    [setupPrintHandlers, openPdfInNewWindow]
  );

  const handleClick = useCallback(async () => {
    let pdfUrl = '';
    try {
      setIsPrinting(true);
      const res = await mutateFunction();
      if (!res.data) {
        console.log('Error downloading pdf: No data returned');
        return;
      }

      pdfUrl = res.data.pdf_url;

      if (printMode && iframeRef.current) {
        await printPdf(pdfUrl);
      } else {
        openPdfInNewWindow(pdfUrl);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error in PDF handling:', error);
      if (pdfUrl) {
        openPdfInNewWindow(pdfUrl);
      }
    } finally {
      setIsPrinting(false);
    }
  }, [mutateFunction, printMode, printPdf, openPdfInNewWindow]);

  if (actionButton) {
    return (
      <ActionButton showLoader={isLoading} icon={icon} title={actionButtonTitle} size="xs" onClick={handleClick} />
    );
  }
  return (
    <Button
      showLoader={isLoading}
      icon={printMode ? Printer : Download}
      onClick={handleClick}
      className="dark:text-primary -mt-2 -mr-4"
      size="sm"
      as="link"
    >
      {printMode ? 'Print' : 'Download'}
    </Button>
  );
}
